import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { toast, confirmar } from '../../lib/toast';
import { Spinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DataTable, type Column } from '../../components/DataTable';
import { AccionComercialBadge } from '../../components/producto/AccionComercial';
import { ACCION_LABEL, type AccionComercial, type Reposicion, type StockMinimo, type UnidadVenta } from '../../../shared/rotacion';

/**
 * Pestaña «Sugerencias» del análisis (CEO, 22/10/2026): analiza el catálogo
 * del cliente y propone
 *  1. qué reponer YA para no romper stock (plazo de reposición + stock mínimo
 *     en escalera ficha › condición comercial › global);
 *  2. qué acción comercial cambiar según la rotación frente al subtipo;
 *  3. tendencias: productos que bajan o mejoran su rotación, para vigilar.
 * Datos: GET /api/analisis/sugerencias?cliente=…; reglas en shared/rotacion.ts.
 */

type Base = {
  id: string; sku: string; nombre: string; estado: string; accion_comercial: AccionComercial;
  categoria_nombre: string | null; subcategoria_nombre: string | null;
  stock_actual: number | null; pendiente: number; media: number | null; media_prev: number | null; media_con_stock: number | null; media_subtipo: number | null;
};
type FilaReponer = Base & { stock_minimo: StockMinimo; reposicion: Reposicion };
type FilaAccion = Base & { sugerida: AccionComercial; motivo: string };
type FilaTendencia = Base & { variacion_pct: number | null };
type Sugerencias = {
  unidad: UnidadVenta | null;
  parametros: { plazo: number; stock_minimo_global: number; banda: number; umbral_tendencia: number };
  reponer: FilaReponer[];
  acciones: FilaAccion[];
  tendencias: { suben: FilaTendencia[]; bajan: FilaTendencia[] };
};

const FUENTE_MINIMO: Record<StockMinimo['fuente'], string> = { producto: 'ficha', condicion: 'condición', global: 'global' };

export function SugerenciasCatalogo({ cliente, base }: { cliente: string; base: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['sugerencias', cliente],
    queryFn: () => api.get<Sugerencias>(`/analisis/sugerencias?cliente=${encodeURIComponent(cliente)}`),
    staleTime: 60_000,
  });
  const d = q.data;
  const u = d?.unidad === 'mes' ? 'uds/mes' : 'uds/sem';
  const venta = (v: number | null) => (v == null ? '—' : fmt.number(Math.round(v * 10) / 10));

  const aplicar = useMutation({
    mutationFn: async (filas: { id: string; sugerida: AccionComercial }[]) => {
      const porAccion = new Map<AccionComercial, string[]>();
      for (const f of filas) porAccion.set(f.sugerida, [...(porAccion.get(f.sugerida) ?? []), f.id]);
      for (const [accion, ids] of porAccion) await api.patch('/productos/bulk', { ids, updates: { accion_comercial: accion } });
      return filas.length;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ['sugerencias'] });
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
      toast.success('Acciones aplicadas', `${n} producto(s) actualizados.`);
    },
    onError: (err) => toast.error('No se pudieron aplicar', (err as Error).message),
  });

  const enlace = (r: Base) => (
    <Link to={`${base}/productos/${r.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs text-accent-700 hover:underline">{r.sku}</Link>
  );
  const nombre = (r: Base) => <span className="block max-w-[16rem] truncate text-xs" title={r.nombre}>{r.nombre}</span>;
  const subtipo = (r: Base) => <span className="text-xs text-slate-600">{r.subcategoria_nombre ?? <span className="text-slate-300">—</span>}</span>;

  const colsReponer: Column<FilaReponer>[] = useMemo(() => [
    { key: 'sku', label: 'SKU', render: enlace, sortValue: (r) => r.sku },
    { key: 'nombre', label: 'Producto', render: nombre, sortValue: (r) => r.nombre },
    { key: 'subcategoria_nombre', label: 'Subtipo', render: subtipo, sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'stock_actual', label: 'Stock', align: 'right', render: (r) => fmt.int(r.stock_actual ?? 0), sortValue: (r) => r.stock_actual ?? 0 },
    { key: 'pendiente', label: 'Pedido pdte.', align: 'right', render: (r) => (r.pendiente ? fmt.int(r.pendiente) : '—'), sortValue: (r) => r.pendiente },
    { key: 'venta', label: 'Venta/sem', align: 'right', render: (r) => fmt.number(r.reposicion.venta_semanal), sortValue: (r) => r.reposicion.venta_semanal },
    {
      key: 'stock_minimo', label: 'Stock mín.', align: 'right',
      render: (r) => <span title={`Origen: ${FUENTE_MINIMO[r.stock_minimo.fuente]}${r.stock_minimo.detalle ? ` (${r.stock_minimo.detalle})` : ''}`}>{fmt.int(r.stock_minimo.valor)} <span className="text-[10px] text-slate-400">{FUENTE_MINIMO[r.stock_minimo.fuente]}</span></span>,
      sortValue: (r) => r.stock_minimo.valor,
    },
    {
      key: 'semanas', label: 'Semanas hasta mínimo', align: 'right',
      render: (r) => <span className={r.reposicion.urgente ? 'font-semibold text-bad-700' : 'text-warn-700'}>{fmt.number(r.reposicion.semanas_hasta_minimo)}</span>,
      sortValue: (r) => r.reposicion.semanas_hasta_minimo,
    },
    { key: 'cantidad', label: 'Pedir', align: 'right', render: (r) => <span className="font-semibold">{fmt.int(r.reposicion.cantidad)}</span>, sortValue: (r) => r.reposicion.cantidad, total: 'sum', totalValue: (r) => r.reposicion.cantidad, totalFormat: (n) => fmt.int(n) },
    {
      key: 'urgente', label: '', sortable: false,
      render: (r) => (r.reposicion.urgente ? <Badge tone="bad">Rompe antes del plazo</Badge> : <Badge tone="warn">Bajo punto de pedido</Badge>),
    },
  ], [base]); // eslint-disable-line react-hooks/exhaustive-deps

  const colsAcciones: Column<FilaAccion>[] = useMemo(() => [
    { key: 'sku', label: 'SKU', render: enlace, sortValue: (r) => r.sku },
    { key: 'nombre', label: 'Producto', render: nombre, sortValue: (r) => r.nombre },
    { key: 'subcategoria_nombre', label: 'Subtipo', render: subtipo, sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'media_con_stock', label: `Venta con stock (${u})`, align: 'right', render: (r) => venta(r.media_con_stock ?? 0), sortValue: (r) => r.media_con_stock ?? 0 },
    { key: 'media_subtipo', label: 'Media subtipo (vigentes)', align: 'right', render: (r) => venta(r.media_subtipo), sortValue: (r) => r.media_subtipo ?? 0 },
    { key: 'stock_actual', label: 'Stock', align: 'right', render: (r) => fmt.int(r.stock_actual ?? 0), sortValue: (r) => r.stock_actual ?? 0 },
    { key: 'accion_comercial', label: 'Acción actual', render: (r) => <AccionComercialBadge accion={r.accion_comercial} />, sortValue: (r) => r.accion_comercial },
    { key: 'sugerida', label: 'Propuesta', render: (r) => <span title={r.motivo}><AccionComercialBadge accion={r.sugerida} /></span>, sortValue: (r) => r.sugerida },
    { key: 'motivo', label: 'Motivo', render: (r) => <span className="text-xs text-slate-500">{r.motivo}</span>, sortValue: (r) => r.motivo },
    {
      key: 'aplicar', label: '', sortable: false, align: 'right',
      render: (r) => (
        <Button size="sm" variant="secondary" disabled={aplicar.isPending}
          onClick={(e) => { e.stopPropagation(); aplicar.mutate([{ id: r.id, sugerida: r.sugerida }]); }}>
          Aplicar
        </Button>
      ),
    },
  ], [base, u, aplicar.isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <div className="py-10"><Spinner /></div>;
  if (q.error) return <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudieron calcular las sugerencias. {(q.error as Error).message}</p>;
  if (!d) return null;
  if (!d.unidad) {
    return <p className="rounded-lg border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">{cliente} no tiene ventas importadas todavía: sin venta media no se pueden calcular sugerencias.</p>;
  }

  const aplicarTodas = async () => {
    if (!(await confirmar({
      title: `¿Aplicar las ${d.acciones.length} acciones propuestas?`,
      message: 'Cambia la acción comercial de cada producto a la propuesta.',
      confirmLabel: 'Aplicar todas',
    }))) return;
    aplicar.mutate(d.acciones.map((a) => ({ id: a.id, sugerida: a.sugerida })));
  };

  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500">
        Venta media {d.unidad === 'mes' ? 'de los 3 últimos meses con venta' : 'de las 4 últimas semanas'} · plazo de reposición <b>{d.parametros.plazo} semanas</b> ·
        stock mínimo: el de la ficha, si no el de la condición comercial (subtipo › tipo › marca), si no el global ({fmt.int(d.parametros.stock_minimo_global)}) ·
        banda neutra ±{d.parametros.banda} % · tendencia ±{d.parametros.umbral_tendencia} %.{' '}
        <Link to="/admin/parametros" className="text-accent-700 underline">Cambiar en Parámetros</Link>
      </p>

      <section>
        <h2 className="mb-1 font-display text-base font-bold text-slate-900">Reponer ya · {d.reponer.length}</h2>
        <p className="mb-2 text-xs text-slate-500">
          Stock + pedido pendiente no cubre la venta de las próximas {d.parametros.plazo} semanas más el stock mínimo. En rojo, los que tocan el mínimo antes de que
          llegue un pedido hecho hoy. «Pedir» es lo que falta para cubrir el plazo y acabar en el mínimo. No incluye los que están en Liquidar.
        </p>
        <DataTable<FilaReponer> columns={colsReponer} rows={d.reponer} rowKey={(r) => r.id} syncUrl={false}
          defaultSort={{ key: 'semanas' }} searchPlaceholder="Buscar SKU o producto…"
          empty={<p className="py-6 text-center text-sm text-slate-500">Nada que reponer: todo cubre el plazo.</p>} />
      </section>

      <section>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-base font-bold text-slate-900">Acciones comerciales propuestas · {d.acciones.length}</h2>
          {d.acciones.length > 1 && <Button size="sm" variant="secondary" onClick={aplicarTodas} disabled={aplicar.isPending}>Aplicar todas</Button>}
        </div>
        <p className="mb-2 text-xs text-slate-500">
          Venta media contando solo {d.unidad === 'mes' ? 'los meses' : 'las semanas'} con stock por encima del mínimo; la media del subtipo, solo con vigentes.
          Rotación por debajo de la media de su subtipo (más de un {d.parametros.banda} %) → <b>{ACCION_LABEL.OFERTA_MEJORAR_ROTACION}</b>; por encima →
          <b> {ACCION_LABEL.MANTENER}</b> si estaba en otra acción. Solo productos vigentes, en fabricación o buscando sustituto.
        </p>
        <DataTable<FilaAccion> columns={colsAcciones} rows={d.acciones} rowKey={(r) => r.id} syncUrl={false}
          defaultSort={{ key: 'sugerida' }} searchPlaceholder="Buscar SKU o producto…"
          empty={<p className="py-6 text-center text-sm text-slate-500">Sin cambios que proponer.</p>} />
      </section>

      <section>
        <h2 className="mb-1 font-display text-base font-bold text-slate-900">Tendencias · para vigilar</h2>
        <p className="mb-2 text-xs text-slate-500">
          Venta media {d.unidad === 'mes' ? 'de los 3 últimos meses frente a los 3 anteriores' : 'de las 4 últimas semanas frente a las 4 anteriores'}, con
          una variación de al menos ±{d.parametros.umbral_tendencia} %. Ordenado por unidades de diferencia.
        </p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ListaTendencia titulo="Bajando su rotación" tono="bad" filas={d.tendencias.bajan} base={base} u={u} />
          <ListaTendencia titulo="Mejorando su rotación" tono="ok" filas={d.tendencias.suben} base={base} u={u} />
        </div>
      </section>
    </div>
  );
}

function ListaTendencia({ titulo, tono, filas, base, u }: { titulo: string; tono: 'ok' | 'bad'; filas: FilaTendencia[]; base: string; u: string }) {
  const venta = (v: number | null) => fmt.number(Math.round((v ?? 0) * 10) / 10);
  return (
    <div className={`rounded-lg border bg-white shadow-sm ${tono === 'bad' ? 'border-bad-300' : 'border-ok-300'}`}>
      <header className={`flex items-center justify-between border-b px-4 py-2 ${tono === 'bad' ? 'border-bad-100 bg-bad-50 text-bad-700' : 'border-ok-100 bg-ok-50 text-ok-700'}`}>
        <h3 className="text-sm font-bold">{tono === 'bad' ? '▼' : '▲'} {titulo}</h3>
        <span className="text-xs">{filas.length}</span>
      </header>
      {filas.length === 0 ? (
        <p className="px-4 py-5 text-center text-xs text-slate-500">Ninguno.</p>
      ) : (
        <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
          {filas.map((f) => (
            <li key={f.id} className="flex items-center gap-3 px-4 py-2 text-xs">
              <Link to={`${base}/productos/${f.id}`} className="font-mono text-accent-700 hover:underline">{f.sku}</Link>
              <span className="min-w-0 flex-1 truncate text-slate-700" title={f.nombre}>{f.nombre}</span>
              <span className="whitespace-nowrap tabular-nums text-slate-500">{venta(f.media_prev)} → <b className="text-slate-900">{venta(f.media)}</b> {u}</span>
              <span className={`w-14 text-right font-semibold tabular-nums ${tono === 'bad' ? 'text-bad-700' : 'text-ok-700'}`}>
                {f.variacion_pct == null ? 'nuevo' : `${f.variacion_pct > 0 ? '+' : ''}${f.variacion_pct} %`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
